import { useToast } from "dialog-hooks"
import { ChangeEvent, useCallback } from "react"
import { useCreateSong, useOpenSong, useSaveSong } from "../actions"
import { iframeConfirm, saveFile, saveFileAs, useOpenFile } from "../actions/file"
import { useLocalization } from "../localize/useLocalization"
import { useAutoSave } from "./useAutoSave"
import { useSong } from "./useSong"

/** True when running inside an iframe (e.g. embedded in MusicWave dashboard) */
const isInIframe = typeof window !== "undefined" && window.parent !== window

export const useSongFile = () => {
  const { isSaved, getSong } = useSong()
  const toast = useToast()
  const localized = useLocalization()
  const createSong = useCreateSong()
  const openSong = useOpenSong()
  const saveSong = useSaveSong()
  const openFile = useOpenFile()
  const { onUserExplicitAction } = useAutoSave()

  return {
    createNewSong: useCallback(async () => {
      if (isSaved || (await iframeConfirm(localized["confirm-new"]))) {
        createSong()
      }
    }, [isSaved, localized, createSong]),
    openSong: useCallback(async () => {
      try {
        if (isSaved || (await iframeConfirm(localized["confirm-open"]))) {
          await openFile()
        }
      } catch (e) {
        toast.error((e as Error).message)
      }
    }, [isSaved, localized, openFile, toast]),
    openSongLegacy: useCallback(
      async (e: ChangeEvent<HTMLInputElement>) => {
        try {
          if (isSaved || (await iframeConfirm(localized["confirm-new"]))) {
            // When embedded, the modern openFile() path already delegates to
            // the parent window — use it here too so the legacy <input> path
            // never triggers a dialog attributed to the Vercel URL.
            if (isInIframe) {
              await openFile()
              return
            }
            await openSong(e.currentTarget)
          }
        } catch (e) {
          toast.error((e as Error).message)
        }
      },
      [isSaved, localized, openFile, openSong, toast],
    ),
    saveSong: useCallback(async () => {
      await saveFile(getSong())
      onUserExplicitAction()
    }, [getSong, onUserExplicitAction]),
    saveAsSong: useCallback(async () => {
      await saveFileAs(getSong())
      onUserExplicitAction()
    }, [getSong, onUserExplicitAction]),
    downloadSong: useCallback(async () => {
      saveSong()
    }, [saveSong]),
  }
}
